// Copy helpers — Content §1 voice, §4 microcopy. One place so wording stays consistent.
import type { CycleStatus, StatusToneName } from './types.js';

export type Tone = StatusToneName;

export function daysUntil(iso: string | null | undefined, now = new Date()): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - now.getTime()) / 86_400_000);
}

export function cycleStatusLabel(status: CycleStatus | null, cutoff?: string | null): string {
  switch (status) {
    case 'open': {
      const days = daysUntil(cutoff);
      if (days === null) return 'Prompts open';
      if (days <= 0) return 'Closing today';
      if (days <= 3) return `Closing in ${days} ${days === 1 ? 'day' : 'days'}`;
      return 'Prompts open';
    }
    case 'closed':
    case 'compiling':
      return 'Edition compiling';
    case 'dropped':
      return 'New edition!';
    case 'awaiting_theme':
      return 'Theme coming soon';
    case 'failed':
      return 'Needs attention';
    default:
      return 'Between cycles';
  }
}

export function cycleTone(status: CycleStatus | null, cutoff?: string | null): Tone {
  if (status === 'dropped') return 'success';
  if (status === 'open') {
    const days = daysUntil(cutoff);
    return days !== null && days <= 3 ? 'warning' : 'active';
  }
  return 'neutral';
}

export function deadlineLabel(cutoff: string | null | undefined): {
  label: string;
  urgent: boolean;
} {
  const days = daysUntil(cutoff);
  if (days === null) return { label: 'Open', urgent: false };
  if (days <= 0) return { label: 'Closes today', urgent: true };
  if (days === 1) return { label: '1 day left', urgent: true };
  return { label: `${days} days left`, urgent: days <= 3 };
}

export function relativeTime(iso: string, now = new Date()): string {
  const diff = now.getTime() - new Date(iso).getTime();
  const minutes = Math.round(diff / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function monthName(yearMonth: string): string {
  const [y, m] = yearMonth.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
}

export function nextMonthName(yearMonth: string): string {
  const [y, m] = yearMonth.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(m === 12 ? y + 1 : y, m === 12 ? 0 : m, 1)).toLocaleDateString(
    undefined,
    { month: 'long' },
  );
}

export function occasionLine(o: {
  subject_name: string;
  occasion_type: string;
  date: string;
  days_until: number;
}) {
  const when =
    o.days_until === 0 ? 'today' : o.days_until === 1 ? 'tomorrow' : `in ${o.days_until} days`;
  return `${o.subject_name}’s ${o.occasion_type} is ${when}`;
}
